import React, { useState } from "react";
import { Button } from "../../../../components/ui/button";

export const PricingSection = (): JSX.Element => {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annually">("monthly");

  const plans = [
    {
      name: "Basic",
      description: "Best for personal use.",
      price: billingCycle === "monthly" ? "$0" : "$0",
      period: billingCycle === "monthly" ? "/ month" : "/ year",
      features: [
        "Employee directory",
        "Task management",
        "Calendar integration",
        "File storage",
        "Communication tools",
        "Reporting and analytics",
      ],
      featured: false,
    },
    {
      name: "Enterprise",
      description: "For large teams & corporations.",
      price: billingCycle === "monthly" ? "$49" : "$490",
      period: billingCycle === "monthly" ? "/ month" : "/ year",
      features: [
        "Advanced employee directory",
        "Project management",
        "Resource scheduling",
        "Version control",
        "Team collaboration",
        "Advanced analytics",
      ],
      featured: true,
    },
    {
      name: "Business",
      description: "Best for business owners.",
      price: billingCycle === "monthly" ? "$99" : "$990",
      period: billingCycle === "monthly" ? "/ month" : "/ year",
      features: [
        "Customizable employee directory",
        "Client project management",
        "Client meeting schedule",
        "Compliance tracking",
        "Client communication",
        "Create custom reports tailored",
      ],
      featured: false,
    },
  ];

  return (
    <section
      id="pricing"
      className="relative w-full py-16 sm:py-20 md:py-24 overflow-hidden bg-black"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 z-0 opacity-30"
        style={{
          backgroundImage: "url('/blue-panes-made-from-glass-blue-colored-light-2 1.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <p className="text-white/60 text-sm sm:text-base md:text-lg mb-2">
            Bring your business to the best scale
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
            Discover Products<br />
            With the Best Pricing
          </h2>
          <p className="text-white/80 text-sm sm:text-base md:text-lg max-w-3xl mx-auto px-2 sm:px-0">
            Select from our best plans, ensuring a perfect match. Need more or less? Customize your subscription for a seamless fit!
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8 sm:mb-10 md:mb-12">
          <div className="bg-[#1A1A1A]/80 rounded-full p-1 inline-flex backdrop-blur-md">
            <button
              className={`px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-medium transition-colors ${
                billingCycle === "monthly" ? "bg-white text-black" : "text-white/80 hover:text-white"
              }`}
              onClick={() => setBillingCycle("monthly")}
            >
              Monthly
            </button>
            <button
              className={`px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-medium transition-colors ${
                billingCycle === "annually" ? "bg-white text-black" : "text-white/80 hover:text-white"
              }`}
              onClick={() => setBillingCycle("annually")}
            >
              Annually
            </button>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
          {plans.map((plan, idx) => (
            <div
              key={idx}
              className={`
                relative
                bg-[#1A1A1A]/80 backdrop-blur-md
                rounded-3xl
                p-6 sm:p-8 md:p-10
                flex flex-col
                border border-white/10
                transition-transform
                ${plan.featured ? "ring-2 ring-white/30 shadow-lg" : "hover:scale-[1.02]"}
              `}
            >
              {/* Plan Icon */}
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-white/20 flex items-center justify-center mb-6">
                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 bg-white rounded-full"></div>
              </div>

              {/* Plan Name & Description */}
              <h3 className="text-white text-xl sm:text-2xl md:text-3xl font-semibold mb-1">
                {plan.name}
              </h3>
              <p className="text-white/60 text-sm sm:text-base mb-6">
                {plan.description}
              </p>

              {/* Price */}
              <div className="flex items-baseline gap-2 mb-8">
                <span className="text-white text-3xl sm:text-4xl md:text-5xl font-semibold">
                  {plan.price}
                </span>
                <span className="text-white/60 text-sm sm:text-base">
                  {plan.period}
                </span>
              </div>

              {/* CTA Button */}
              <Button
                className={`w-full mb-8 sm:mb-10 ${
                  plan.featured
                    ? "bg-white text-black hover:bg-white/90"
                    : "bg-[#333]/80 text-white hover:bg-[#444]/80"
                } rounded-full py-3 sm:py-3.5 text-sm sm:text-base font-medium transition`}
              >
                Get Started
              </Button>

              {/* Features List */}
              <div className="mt-auto">
                <h4 className="text-white text-sm sm:text-base font-medium mb-4">
                  What you will get
                </h4>
                <ul className="space-y-3 sm:space-y-4">
                  {plan.features.map((feature, fIdx) => (
                    <li
                      key={fIdx}
                      className="flex items-center gap-3 text-white/60 text-sm sm:text-base"
                    >
                      {/* Simple dot icon (circle outline) */}
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="flex-shrink-0"
                      >
                        <circle
                          cx="8"
                          cy="8"
                          r="7.5"
                          stroke="currentColor"
                          strokeOpacity="0.2"
                        />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
